// src/lib/avoir-html.js — Gabarit HTML de l'AVOIR (pièce rectificative).
//
// Chantier « L'avoir a un bouton ». Un avoir est une pièce comptable NEUVE qui
// annule une facture émise : il porte son propre numéro (série AV-{prefix}),
// un montant NÉGATIF, et il NOMME la pièce qu'il annule. La facture d'origine
// n'est ni modifiée ni effacée — le document le dit en toutes lettres, parce
// que c'est ce qu'un lecteur (proprio, comptable, contrôleur) doit comprendre.
//
// Fonction PURE, sans I/O, même charte que src/lib/facture-html.js (navy
// #215387, Playfair Display / Raleway, logo par placeholder __LOGO_B64__ à
// gonfler via inflateFactureSnapshot avant tout rendu).
//
// ⚠️ LE NUMÉRO ET LA DATE NE SONT PAS CONNUS ICI. Le HTML est figé AVEC les
// jetons __INVOICE_NUMBER__ / __INVOICE_DATE__ ; c'est la RPC emit_credit_note
// (migration 20270114000001) qui les substitue DANS LA MÊME TRANSACTION que le
// tirage du numéro. Ne jamais les remplacer côté client : un numéro « prévu »
// peut diverger du numéro réellement tiré (compteur dynamique, second onglet).

import {
  INVOICE_NUMBER_PLACEHOLDER,
  INVOICE_DATE_PLACEHOLDER,
} from "./facture-html";
import { numeroTvaImprimable } from "./emitter-resolver";

const LOGO_PLACEHOLDER = "__LOGO_B64__";

// Échappement minimal : les libellés (noms de proprio/logement, mois) viennent
// de cfg/meta — jamais interprétés comme du HTML.
const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtEur = (n) =>
  `${Number(n || 0)
    .toFixed(2)
    .replace(".", ",")} €`;

/**
 * Génère le HTML de l'avoir.
 *
 * @param {object} args
 * @param {object} args.original  - la facture annulée, telle que l'Historique
 *   la porte : { num (numéro réel), date (émission, 'DD/MM/YYYY'), montant,
 *   mois, prop, log }
 * @param {object} args.cfg       - blob fbz8-cfg (cfg.co : adresse, siret,
 *   email, tel, tva, numeroTva)
 *
 * ⚠️ PAS de paramètre `motif` : il a existé, personne ne l'a jamais passé
 * (l'unique appelant est App.jsx::emitAvoir, qui ne donne qu'original + cfg),
 * et la ligne « Motif : … » qu'il aurait rendue n'est donc jamais apparue sur
 * aucun avoir. Un paramètre mort sur une pièce comptable finit par être
 * rebranché à la hâte, sans que personne ne sache ce qu'il écrit dans un
 * document que le trigger d'immuabilité interdit de corriger. Rouvrir ce
 * champ = décision Dimitri (qui écrit le motif, et qui le relit avant envoi).
 *
 * @returns {{ html: string, montant: number }} montant = valeur NÉGATIVE de
 *   l'avoir (−|montant de la facture|)
 */
export function genAvoirHTML({ original, cfg }) {
  if (!original || !original.num) {
    throw new Error(
      "avoir-html : la facture annulée doit porter un numéro (un brouillon ne s'annule pas, il se supprime)",
    );
  }
  const montantOriginal = Number(original.montant);
  if (!Number.isFinite(montantOriginal)) {
    throw new Error(
      "avoir-html : montant de la facture annulée incalculable — aucun avoir ne doit porter un montant faux",
    );
  }
  const montant = -Math.abs(montantOriginal);
  // Numéro de TVA : jamais écrit en dur (décisions du 28/10, n° 25 et 34 ;
  // consigne Dimitri du 29/10). Seulement le numéro SAISI, si la conciergerie
  // est assujettie (règle unique : numeroTvaImprimable) ; sinon la mention
  // 293 B. Garde : src/lib/tva-jamais-imprimee.test.js.
  const numeroTva = numeroTvaImprimable(cfg?.co);

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Avoir ${INVOICE_NUMBER_PLACEHOLDER}</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Raleway:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4;margin:0.5cm}
  body{font-family:'Raleway',sans-serif;background:#eef2f7;color:#333;padding:0}
  .page{background:#fff;max-width:700px;margin:0 auto;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)}
  .header{background:linear-gradient(135deg,#1a3f66,#215387,#3a72a8);color:#fff;padding:14px 24px;display:flex;justify-content:space-between;align-items:center}
  .logo-area{display:flex;align-items:center;gap:14px}
  .logo{width:52px;height:52px;border-radius:50%;object-fit:cover;border:2px solid rgba(255,255,255,.3)}
  .company h1{font-family:'Playfair Display',serif;font-size:18px;font-weight:700;letter-spacing:.5px}
  .company p{font-size:11px;opacity:.7;margin-top:2px}
  .fact-num{text-align:right}
  .fact-num .kind{font-size:10px;letter-spacing:1px;text-transform:uppercase;opacity:.8}
  .fact-num .num{font-family:'Playfair Display',serif;font-size:16px;font-weight:700}
  .fact-num .date{font-size:11px;opacity:.7;margin-top:4px}
  .body{padding:14px 24px}
  .meta{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:10px}
  .meta-box{background:#f8fafd;border-radius:10px;padding:10px 12px}
  .meta-box h3{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;font-weight:700}
  .meta-box p{font-size:11px;color:#333;line-height:1.4}
  .meta-box strong{font-weight:700;color:#215387}
  .section-title{font-family:'Playfair Display',serif;font-size:12px;color:#215387;font-weight:700;margin-bottom:6px;padding-bottom:4px;border-bottom:2px solid #eef2f7}
  .ref{background:#fff8e6;border:1px solid #e9c46a;border-radius:10px;padding:10px 12px;margin-bottom:10px;font-size:11px;line-height:1.5;color:#444}
  .ref strong{color:#215387}
  table{width:100%;border-collapse:collapse;margin-bottom:8px;font-size:11px}
  th{background:#f0f4f8;padding:5px 8px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.4px;color:#555;font-weight:700}
  th.r,td.r{text-align:right}
  td{padding:5px 8px;border-bottom:1px solid #f0f4f8;color:#444}
  .total-box{background:#87b8e6;border:2px solid #87b8e6;color:#fff;border-radius:10px;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
  .total-box .label{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
  .total-box .amount{font-family:'Playfair Display',serif;font-size:22px;font-weight:800}
  .footer{text-align:center;font-size:9px;color:#bbb;padding:8px;border-top:1px solid #f0f4f8}
  @media print{body{padding:0;background:#fff}.page{box-shadow:none;border-radius:0;max-width:100%}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="logo-area">
      <img src="${LOGO_PLACEHOLDER}" class="logo" alt="logo"/>
      <div class="company">
        <h1>FULLBNBZEN</h1>
        <p>SAS au capital de 100&nbsp;€</p>
        <p>${esc(cfg?.co?.adresse)}</p>
      </div>
    </div>
    <div class="fact-num">
      <div class="kind">Avoir</div>
      <div class="num">N° ${INVOICE_NUMBER_PLACEHOLDER}</div>
      <div class="date">Émis le ${INVOICE_DATE_PLACEHOLDER}</div>
      <div class="date" style="margin-top:4px">Période : ${esc(original.mois)}</div>
    </div>
  </div>
  <div class="body">
    <div class="meta">
      <div class="meta-box">
        <h3>Émetteur</h3>
        <p><strong>FULLBNBZEN SAS</strong><br>
        ${esc(cfg?.co?.adresse)}<br>
        SIRET : ${esc(cfg?.co?.siret || "927 602 805 00016")}<br>
        RCS Toulouse 927 602 805<br>
        ${
          numeroTva
            ? `N°TVA : ${esc(numeroTva)}<br>`
            : `<span style="font-size:11px;color:#888;font-style:italic">TVA non applicable, art. 293 B du CGI</span>`
        }</p>
        <p style="margin-top:6px">${esc(cfg?.co?.email)}${cfg?.co?.tel ? `<br>Tél : ${esc(cfg.co.tel)}` : ""}</p>
      </div>
      <div class="meta-box">
        <h3>Client</h3>
        <p><strong>${esc(original.prop)}</strong><br>
        ${esc(original.log)}</p>
      </div>
    </div>

    <div class="ref">
      Cet avoir <strong>annule intégralement</strong> la facture <strong>N° ${esc(original.num)}</strong>
      émise le ${esc(original.date || "—")} pour la période ${esc(original.mois)}, d'un montant de ${esc(fmtEur(montantOriginal))}.
      La facture d'origine est conservée dans nos archives et n'est pas modifiée ; le présent avoir en est la contrepartie.
    </div>

    <div class="section-title">Détail</div>
    <table>
      <thead><tr><th>Libellé</th><th class="r">Montant</th></tr></thead>
      <tbody>
        <tr>
          <td>Annulation de la facture N° ${esc(original.num)} — ${esc(original.mois)} — ${esc(original.log)}</td>
          <td class="r">${esc(fmtEur(montant))}</td>
        </tr>
      </tbody>
    </table>

    <div class="total-box">
      <span class="label">Total avoir</span>
      <span class="amount">${esc(fmtEur(montant))}</span>
    </div>
    <p style="font-size:10px;color:#777;line-height:1.5">Montant à porter au crédit du client. Aucun règlement n'est attendu au titre de cette pièce.</p>
  </div>
  <div class="footer">FULLBNBZEN SAS — avoir émis en application de l'article 289 du CGI (numérotation continue, pièce rectificative)</div>
</div>
</body>
</html>`;

  return { html, montant };
}
